type ThrottledFn<T extends (...args: Parameters<T>) => void> = {
  (...args: Parameters<T>): void
  cancel(): void
}

/**
 * Returns a throttled version of `fn` that invokes at most once per `wait` ms.
 * Executes on the leading edge immediately, then on the trailing edge if called
 * again during the wait window — matching the default lodash.throttle behaviour.
 * The returned function has a `.cancel()` method to discard any pending trailing call.
 */
export function throttle<T extends (...args: Parameters<T>) => void>(fn: T, wait: number): ThrottledFn<T> {
  let lastRan = 0
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let pendingArgs: Parameters<T> | null = null

  const invoke = (args: Parameters<T>) => {
    lastRan = Date.now()
    fn(...args)
  }

  const throttled = (...args: Parameters<T>): void => {
    const remaining = wait - (Date.now() - lastRan)
    pendingArgs = args

    if (remaining <= 0) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      invoke(args)
      pendingArgs = null
    } else if (timeoutId === null) {
      timeoutId = setTimeout(() => {
        timeoutId = null
        if (pendingArgs !== null) {
          invoke(pendingArgs)
          pendingArgs = null
        }
      }, remaining)
    }
  }

  throttled.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    pendingArgs = null
    lastRan = 0
  }

  return throttled
}
